import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSupportTickets } from "../../services/supportService";
import { SkeletonCard } from "../../components/Skeleton/Skeleton";
import TicketStatusBadge from "../../components/support/TicketStatusBadge";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { formatRelativeTime } from "../../utils/helpers/dateDisplay";

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";

const NewTicketLink = ({ children }) => (
  <Link
    to="/support/new"
    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors ${FOCUS_RING}`}
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
    </svg>
    {children}
  </Link>
);

// "My requests" — every ticket the signed-in user opened, newest activity
// first. A dot marks a support reply the user has not opened yet.
export default function SupportList() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    getSupportTickets({ params: { page }, signal: controller.signal })
      .then((res) => setData(res))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setError(formatApiError(err, t("support.loadFailed")));
      });
    return () => controller.abort();
  }, [page, t]);

  const rows = data?.results || [];

  return (
    <div className="flex-1 bg-slate-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500 font-medium max-w-xl">{t("support.subtitle")}</p>
          <NewTicketLink>{t("support.newTicket")}</NewTicketLink>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 font-semibold">
            {error}
          </div>
        ) : data === null ? (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-[32px] border-2 border-dashed border-slate-200 bg-white/60 py-16 px-6 text-center">
            <p className="text-sm font-black text-slate-700">{t("support.emptyTitle")}</p>
            <p className="text-xs text-slate-500 font-medium mt-2 max-w-md mx-auto">
              {t("support.emptyBody")}
            </p>
            <div className="mt-6">
              <NewTicketLink>{t("support.newTicket")}</NewTicketLink>
            </div>
          </div>
        ) : (
          <>
            <ul className="space-y-3">
              {rows.map((ticket) => (
                <li key={ticket.uuid}>
                  <Link
                    to={`/support/${ticket.uuid}`}
                    className={`flex items-center gap-4 bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 hover:border-indigo-200 hover:shadow-md transition ${FOCUS_RING}`}
                  >
                    <span className="w-2.5 shrink-0 flex justify-center">
                      {ticket.has_unread && (
                        <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" aria-hidden="true" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                        #{ticket.number} · {t(`support.category.${ticket.category}`)}
                      </span>
                      <span className={`block truncate text-sm mt-0.5 ${ticket.has_unread ? "font-black text-slate-900" : "font-semibold text-slate-800"}`}>
                        {ticket.subject}
                      </span>
                      {ticket.has_unread && (
                        <span className="sr-only">{t("support.unread")}</span>
                      )}
                    </span>
                    <span className="flex flex-col items-end gap-1 shrink-0">
                      <TicketStatusBadge status={ticket.status} />
                      <span className="text-[11px] text-slate-400 font-medium">
                        {formatRelativeTime(ticket.last_message_at)}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            {(data.previous || data.next) && (
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={!data.previous}
                  className={`px-4 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-600 disabled:opacity-40 ${FOCUS_RING}`}
                >
                  {t("support.prevPage")}
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data.next}
                  className={`px-4 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-600 disabled:opacity-40 ${FOCUS_RING}`}
                >
                  {t("support.nextPage")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
